import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Banknote,
  Check,
  CircleCheck,
  FilePenLine,
  HandCoins,
  Inbox,
  Pencil,
  Printer,
  Send,
  Trash2,
  Undo2,
  XCircle,
} from 'lucide-react';

import {
  cancelPaymentRequest,
  closePaymentRequest,
  deleteDraftPaymentRequest,
  payPaymentRequest,
  printPaymentRequest,
  receivePaymentRequest,
  reviewPaymentRequest,
  startPaymentRequestProcessing,
  submitPaymentRequest,
  updatePaymentRequestRemark,
} from '#/api/fmsMgmt/paymentRequest';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FmsDocumentCommandBarActions from '#/modules/finMgmt/shared/components/FmsDocumentCommandBarActions';
import FmsRemarkDialog from '#/modules/finMgmt/shared/components/FmsRemarkDialog';
import useFmsPrintPdf from '#/modules/finMgmt/shared/hooks/useFmsPrintPdf';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  paymentRequestDetailQueryKey,
  paymentRequestListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import {
  getPaymentRequestApiErrorMessage,
  isPaymentRequestVersionConflict,
} from '../helper/apiError';
import {
  canCancelPaymentRequest,
  canClosePaymentRequest,
  canDeletePaymentRequest,
  canEditPaymentRequest,
  canPayPaymentRequest,
  canReceivePaymentRequest,
  canReviewPaymentRequest,
  canStartProcessingPaymentRequest,
  canSubmitPaymentRequest,
} from '../helper/paymentRequestStatus';

/**
 * personal：個人中心（填單人／領款人）— 編輯、提交、確認領收、刪除草稿。
 * finance：財務管理（會計審核）— 開始處理、核准／退回、撥款、結案。
 * 取消與改備註兩邊都有（依權限）。
 */
const EXPOSED_ORDER = {
  personal: ['edit', 'submit', 'receive', 'print'],
  finance: ['startProcessing', 'approve', 'reject', 'pay', 'close', 'print'],
};

const ACTION_GROUPS = [
  { key: 'content', label: '單據內容', ids: ['edit', 'editRemark', 'print'] },
  {
    key: 'status',
    label: '單據狀態',
    ids: [
      'submit',
      'receive',
      'startProcessing',
      'approve',
      'reject',
      'pay',
      'close',
      'cancel',
      'deleteDraft',
    ],
  },
];

const PERSONAL_ONLY = new Set(['edit', 'submit', 'receive', 'deleteDraft']);
const FINANCE_ONLY = new Set(['startProcessing', 'approve', 'reject', 'pay', 'close']);

function pickUpdatedAt(detail) {
  return detail?.record?.updatedAt ?? detail?.updatedAt ?? null;
}

const PaymentRequestCommandBar = ({ detail, mode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirmDialog();
  const currentUserId = useSelector((state) => state.infra.user?.id);
  const canUpdate = useCan(PERM.FMS_PAYMENT_REQUEST_UPDATE);
  const canDelete = useCan(PERM.FMS_PAYMENT_REQUEST_DELETE);
  const canSubmit = useCan(PERM.FMS_PAYMENT_REQUEST_SUBMIT);
  const canProcess = useCan(PERM.FMS_PAYMENT_REQUEST_PROCESS);
  const canReview = useCan(PERM.FMS_PAYMENT_REQUEST_REVIEW);
  const canPay = useCan(PERM.FMS_PAYMENT_REQUEST_PAY);
  const canReceive = useCan(PERM.FMS_PAYMENT_REQUEST_RECEIVE);
  const canClose = useCan(PERM.FMS_PAYMENT_REQUEST_CLOSE);
  const canCancel = useCan(PERM.FMS_PAYMENT_REQUEST_CANCEL);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [remarkOpen, setRemarkOpen] = useState(false);

  const id = detail?.id;
  const status = detail?.status;
  const isPayee = Boolean(currentUserId)
    && String(detail?.payee?.id ?? detail?.payeeId ?? '') === String(currentUserId);
  const backTo = mode === 'finance'
    ? ROUTES.fmsPaymentRequest
    : ROUTES.personalCenterClaimsPanel;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: paymentRequestDetailQueryKey(id) });
    await queryClient.invalidateQueries({ queryKey: paymentRequestListQueryKeyPrefix() });
  }

  const commandMutation = useMutation({
    mutationFn: ({ action, payload }) => {
      if (action === 'submit') {
        return submitPaymentRequest(id);
      }
      if (action === 'startProcessing') {
        return startPaymentRequestProcessing(id);
      }
      if (action === 'approve') {
        return reviewPaymentRequest(id, { approved: true });
      }
      if (action === 'reject') {
        return reviewPaymentRequest(id, { approved: false, rejectReason: payload.rejectReason });
      }
      if (action === 'pay') {
        return payPaymentRequest(id);
      }
      if (action === 'receive') {
        return receivePaymentRequest(id);
      }
      if (action === 'close') {
        return closePaymentRequest(id);
      }
      if (action === 'cancel') {
        return cancelPaymentRequest(id, { cancelReason: payload.cancelReason });
      }
      if (action === 'deleteDraft') {
        return deleteDraftPaymentRequest(id);
      }
      return updatePaymentRequestRemark(id, {
        remark: payload.remark,
        updatedAt: pickUpdatedAt(detail),
      });
    },
    onSuccess: async (result, { action }) => {
      if (action === 'deleteDraft') {
        toast.success('已刪除請款草稿');
        await queryClient.invalidateQueries({ queryKey: paymentRequestListQueryKeyPrefix() });
        navigate(backTo);
        return;
      }
      toast.success(result?.message || '操作成功');
      setRejectOpen(false);
      setCancelOpen(false);
      setRemarkOpen(false);
      await refresh();
    },
    onError: (error) => {
      toast.error(getPaymentRequestApiErrorMessage(error, '操作失敗'));
      if (isPaymentRequestVersionConflict(error)) {
        refresh();
      }
    },
  });

  const pending = commandMutation.isPending;

  async function confirmThen(action, options) {
    const ok = await confirm(options);
    if (ok) {
      commandMutation.mutate({ action });
    }
  }

  const { isPrinting, print } = useFmsPrintPdf(
    () => printPaymentRequest(id, { requestNo: detail?.requestNo }),
    getPaymentRequestApiErrorMessage,
  );

  const allActions = {
    print: {
      id: 'print',
      label: isPrinting ? '列印中…' : '列印',
      icon: Printer,
      available: Boolean(id),
      disabled: isPrinting,
      showExposedIcon: true,
      exposedVariant: 'outline',
      onSelect: print,
    },
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: ROUTES.personalCenterClaimEdit(id),
      available: canUpdate && canEditPaymentRequest(status),
      showExposedIcon: true,
      exposedClassName: 'document-detail-edit-action',
    },
    editRemark: {
      id: 'editRemark',
      label: '編輯備註',
      icon: FilePenLine,
      available: canUpdate,
      showExposedIcon: true,
      exposedVariant: 'outline',
      onSelect: () => setRemarkOpen(true),
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canSubmit && canSubmitPaymentRequest(status),
      onSelect: () => confirmThen('submit', {
        title: '提交請款單',
        message: '提交後會產生正式單號並送交財務，確定提交？',
        confirmText: '提交',
      }),
    },
    receive: {
      id: 'receive',
      label: '確認領收',
      icon: Inbox,
      available: canReceive && isPayee && canReceivePaymentRequest(status),
      onSelect: () => confirmThen('receive', {
        title: '確認領收',
        message: '確認已收到這筆款項？',
        confirmText: '確認領收',
      }),
    },
    startProcessing: {
      id: 'startProcessing',
      label: '開始處理',
      icon: HandCoins,
      available: canProcess && canStartProcessingPaymentRequest(status),
      onSelect: () => confirmThen('startProcessing', {
        title: '開始財務處理',
        message: '受理這張請款單並開始審核？',
        confirmText: '開始處理',
      }),
    },
    approve: {
      id: 'approve',
      label: '核准',
      icon: Check,
      available: canReview && canReviewPaymentRequest(status),
      onSelect: () => confirmThen('approve', {
        title: '核准請款單',
        message: '確認請款內容合理且單據與憑證相符？核准後即可撥款。',
        confirmText: '核准',
      }),
    },
    reject: {
      id: 'reject',
      label: '退回',
      icon: Undo2,
      available: canReview && canReviewPaymentRequest(status),
      destructive: true,
      onSelect: () => setRejectOpen(true),
    },
    pay: {
      id: 'pay',
      label: '撥款',
      icon: Banknote,
      available: canPay && canPayPaymentRequest(status),
      onSelect: () => confirmThen('pay', {
        title: '撥款',
        message: '確認已完成撥款？',
        confirmText: '撥款',
      }),
    },
    close: {
      id: 'close',
      label: '結案',
      icon: CircleCheck,
      available: canClose && canClosePaymentRequest(status),
      onSelect: () => confirmThen('close', {
        title: '結案',
        message: '領款人已確認領收，確定結案？',
        confirmText: '結案',
      }),
    },
    cancel: {
      id: 'cancel',
      label: '取消請款',
      icon: XCircle,
      available: canCancel && canCancelPaymentRequest(status),
      destructive: true,
      onSelect: () => setCancelOpen(true),
    },
    deleteDraft: {
      id: 'deleteDraft',
      label: '刪除草稿',
      icon: Trash2,
      available: canDelete && canDeletePaymentRequest(status),
      destructive: true,
      onSelect: () => confirmThen('deleteDraft', {
        title: '刪除請款草稿',
        message: '草稿刪除後無法復原，確定刪除？',
        confirmText: '刪除',
        destructive: true,
      }),
    },
  };

  const hidden = mode === 'finance' ? PERSONAL_ONLY : FINANCE_ONLY;
  const actionMap = Object.fromEntries(
    Object.entries(allActions).map(([key, action]) => [
      key,
      hidden.has(key) ? { ...action, available: false } : action,
    ]),
  );

  return (
    <>
      {confirmDialog}
      <FmsDocumentCommandBarActions
        actionMap={actionMap}
        actionGroups={ACTION_GROUPS}
        workflowExposedOrder={EXPOSED_ORDER[mode]}
        isPending={pending}
      />

      <DocumentRejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="退回請款單"
        description="退回後由填單人修改再重新提交。"
        isPending={pending}
        onConfirm={(rejectReason) => {
          commandMutation.mutate({ action: 'reject', payload: { rejectReason } });
        }}
      />
      <DocumentRejectReasonDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="取消請款單"
        description="取消後流程作廢，無法再提交。"
        confirmText="取消請款"
        reasonLabel="取消原因"
        emptyErrorMessage="請填寫取消原因"
        isPending={pending}
        onConfirm={(cancelReason) => {
          commandMutation.mutate({ action: 'cancel', payload: { cancelReason } });
        }}
      />
      <FmsRemarkDialog
        open={remarkOpen}
        onOpenChange={setRemarkOpen}
        initialRemark={detail?.remark ?? ''}
        isPending={pending}
        inputId="paymentRequestRemark"
        onSave={(remark) => commandMutation.mutate({ action: 'remark', payload: { remark } })}
      />
    </>
  );
};

PaymentRequestCommandBar.propTypes = {
  detail: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(['personal', 'finance']).isRequired,
};

export default PaymentRequestCommandBar;
